import { useContext, useMemo } from 'react';
import { AdminTable, AdminTableContext } from '../../AdminTable';
import { AdminOrganizations } from '@platform/client';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { AdminOrgTeamMatchRecord } from '@platform/client/schemas';
import { ProjectLink } from '../projects/ProjectsAdmin';
import { NoRecordsFoundTabContent, ProfileTabContainer } from '../../components/ProfileComponents';
import { formatDateStr } from '../../../dashboard/my-projects/utils';
import { TeamLink } from '../teams/TeamsResultsTab';

const columnHelper = createColumnHelper<AdminOrgTeamMatchRecord>();
export const TeamMatchingTabContent = ({ organizationId }: { organizationId: number }) => {
  const { adminQueryOptionsV2 } = useContext(AdminTableContext);
  const {
    data: teamMatchData,
    isSuccess,
    isLoading,
    isError,
  } = AdminOrganizations.useGetOrganizationTeamMatchData({
    organization_id: organizationId,
    page: adminQueryOptionsV2.page,
    limit: adminQueryOptionsV2.limit,
    sort: adminQueryOptionsV2.sort,
  });
  const columns = useMemo(
    () =>
      [
        columnHelper.accessor('discoveryId', {
          header: 'Id',
          cell: (info) => info.getValue(),
          size: 5,
        }),
        columnHelper.accessor('representativeName', {
          header: 'Representative',
          cell: (info) => info.getValue(),
          size: 150,
        }),
        columnHelper.accessor('projectName', {
          header: 'Project',
          cell: ({ row }) => (
            <ProjectLink
              projectId={row.original.projectId}
              projectName={row.original.projectName}
            />
          ),
          size: 300,
        }),
        columnHelper.accessor('teamId', {
          header: 'Team',
          cell: ({ row }) => (
            <TeamLink teamId={row.original.teamId} cohortId={row.original.cohortId} />
          ),
          size: 10,
        }),
        columnHelper.accessor('myProjectsStatus', {
          header: 'Status',
          cell: (info) => info.getValue(),
          size: 10,
        }),
        columnHelper.accessor('myProjectsStatusDate', {
          header: 'Status date',
          cell: (info) => formatDateStr(info.getValue()),
          size: 150,
        }),
      ] as ColumnDef<AdminOrgTeamMatchRecord>[],
    [],
  );

  return (
    <ProfileTabContainer>
      {isSuccess && teamMatchData?.matchRecords.length === 0 ?
        <NoRecordsFoundTabContent recordType='organization' />
      : <AdminTable
          isError={isError}
          isLoading={isLoading}
          isSuccess={isSuccess}
          columns={columns}
          data={teamMatchData?.matchRecords || []}
          isSimple
          filteredCount={teamMatchData?.totalCount ?? 0}
        />
      }
    </ProfileTabContainer>
  );
};
